import React, { useEffect, useCallback } from 'react';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { IoTrash, IoChevronUp } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { useDeleteEtaiementPhase, useUpdateEtaiementPhase } from '~/hooks/etaiements';
import styles from './phase-form.module.scss';

import {
  InputText,
} from '~/lib/HooksFormFields';
import { IPhase } from '~/types/estimates';

import DimensionForm from './DimensionForm';
import MobilizationForm from './MobilizationForm';
import RentalsForm from './RentalsForm';
import TransportForm from './TransportForm';
import HandlingForm from './HandlingForm';
import PrestationsForm from './PrestationsForm';
import StudyForm from './StudyForm';
import withModalConfirm from '~/lib/withModalConfirm';
import Summary from '~/components/Summary';
import { sanitizePrestationsForPersistence } from '~/utils/etaiement-prestations';
import { sanitizeRentalsForPersistence } from '~/utils/etaiement-rentals';
import { groupEtaiementSummarySteps } from '~/utils/summary-groups';

const PhaseForm = ({
  etaiementId,
  phase,
  isPreview,
  confirm,
} : {
  etaiementId: string,
  phase: IPhase,
  isPreview: boolean,
  confirm: (message: string, action?: () => void, cancel?: () => void) => void
}) => {
  const navigate = useNavigate();
  const {
    mutate: updateEtaiementPhase,
  } = useUpdateEtaiementPhase('Les modifications ont été prise en compte');

  const {
    mutate: deleteEtaiementPhase,
  } = useDeleteEtaiementPhase();

  const handleDelete = () => {
    if (isPreview) return;
    confirm(
      'Êtes-vous sûr de vouloir supprimer cette phase?',
      async () => {
        await deleteEtaiementPhase({
          etaiementId,
          phaseId: phase._id,
        });
        navigate(`/etaiements/${etaiementId}`);
        const layout = document?.getElementById('layout');
        if (layout) {
          layout.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      },
      () => null,
    );
  };

  const methods = useForm({
    defaultValues: {
      ...phase,
      prestations: (phase as any)?.prestations || [],
      rentals: (phase as any)?.rentals || [],
    },
  });

  const submit = useCallback(async (data: FieldValues) => {
    if (isPreview) return;
    updateEtaiementPhase({
      etaiementId,
      _id: phase._id,
      ...data,
      prestations: sanitizePrestationsForPersistence(data.prestations),
      rentals: sanitizeRentalsForPersistence(data.rentals),
    });
  }, [phase, etaiementId, isPreview, updateEtaiementPhase]);

  const debouncedSubmit = useDebounceCallback(submit, 1500);

  // function to watch change on all field
  const submitOnChange = () => methods.watch(() => {
    methods.handleSubmit(debouncedSubmit)();
  });

  const scrollToTop = () => {
    const layout = document?.getElementById('layout');
    if (layout) {
      layout.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    // const subscription = watch((data) => {
    const subscription = submitOnChange();
    return () => subscription.unsubscribe();
  }, [submitOnChange]);

  return (
    <FormProvider {...methods}>
      <div className={styles.form}>
        <div id="title" className={`${styles.containerField} ${styles.name}`}>
          {isPreview
            ? <h1>{phase.name}</h1>
            : <InputText
            name='name'
            control={methods.control}
          />
          }
        </div>
        <div className={styles.containerField}>
        {isPreview
          ? <p>{phase.description}</p>
          : <InputText
            name='description'
            label='Description'
            placeholder='Description de la phase...'
            control={methods.control}
          />
        }
        </div>
        <section>
          <DimensionForm isPreview={isPreview} />
          <hr />
          <HandlingForm isPreview={isPreview} />
          <hr />
          <TransportForm isPreview={isPreview} />
          <hr />
          <PrestationsForm isPreview={isPreview} />
          <hr />
          <RentalsForm isPreview={isPreview} />
          <hr />
          <MobilizationForm isPreview={isPreview} />
          <hr />
          <StudyForm isPreview={isPreview} />
        </section>
        {phase.summary
          && <>
            <hr />
            <section>
            <Summary summary={phase.summary} groupSteps={groupEtaiementSummarySteps} />
            </section>
          </>
        }

        {!isPreview
          && <button
            type="button"
            className={styles.delete}
            onClick={() => handleDelete()}
          >
            {IoTrash({})} Supprimer la phase
          </button>
        }
        <button
            type="button"
            className={styles.scrollToTop}
            onClick={scrollToTop}
            title="Remonter en haut"
          >
          {IoChevronUp({})}
        </button>
      </div>
    </FormProvider>
  );
};

export default withModalConfirm(PhaseForm);
